import { Suspense, useEffect } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'

import { F0Box } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { AtsJobApplicationPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { JobApplicationCvSection } from './CvSection'

const JobApplicationPage = observer(() => {
  const { company, access } = useCurrentSession()
  const params = useParams()
  const applicationId = String(params.application_id)
  const jobId = String(params.jobId)
  const { navigation } = useNavigation()
  const location = useLocation()
  const navigate = useNavigate()

  const { data, loading } = useQuery(AtsJobApplicationPageDocument, {
    variables: { accessId: access.id, applicationId, companyId: company.id },
  })

  const application = data?.ats.application
  const canManageJobPostings = data?.ats._policies.manageJobPostings.can ?? false
  const hiringRole = application?.jobPosting?.currentHiringRole[0]?.hiringRole ?? 'editor'
  const isReviewerRole = hiringRole === 'reviewer' && !canManageJobPostings

  const allLoaded = !loading && application && data

  // Redirect reviewer role to notes page when on index route
  useEffect(() => {
    if (isReviewerRole) {
      const isOnIndexRoute = location.pathname.includes(
        navigation.ats.jobs.jobsId.applications.applicationDetail.index.fullPath({
          jobId,
          application_id: applicationId,
        })
      )
      const isOnNotesRoute = location.pathname.includes(
        navigation.ats.jobs.jobsId.applications.applicationDetail.notes.fullPath({
          jobId,
          application_id: applicationId,
        })
      )

      if (isOnIndexRoute && !isOnNotesRoute) {
        track(trackingEvents.ats.reviewerCandidateViewed, {
          ats_application_id: applicationId,
          ats_job_id: jobId,
        })
        navigate(
          navigation.ats.jobs.jobsId.applications.applicationDetail.notes.fullPath({
            jobId,
            application_id: applicationId,
          }),
          { replace: true }
        )
      }
    }
  }, [isReviewerRole, jobId, applicationId])

  if (!allLoaded) {
    return (
      <F0Box display="flex" alignItems="center" justifyContent="center" height="full" paddingY="sm">
        <Spinner size="large" />
      </F0Box>
    )
  }

  return (
    <F0Box
      display="flex"
      flexDirection="column"
      height="full"
      overflow="hidden"
      position="relative"
    >
      <F0Box
        display="flex"
        flexDirection="row"
        grow
        overflow="hidden"
        position="relative"
        minHeight="0"
        borderTop={isReviewerRole ? 'default' : undefined}
      >
        <F0Box
          display="flex"
          flexDirection="column"
          shrink={false}
          width="1/2"
          overflowX="hidden"
          overflowY="auto"
          borderRight="default"
          position="relative"
        >
          {/* TO DO: if each tab page has a skeleton, we can remove the fallback */}
          <Suspense fallback={<Spinner size="large" />}>
            <Outlet context={{ application, isReviewerRole, hiringRole }} />
          </Suspense>
        </F0Box>
        <F0Box display="flex" flexDirection="column" grow overflowY="auto">
          <JobApplicationCvSection data={application} />
        </F0Box>
      </F0Box>
    </F0Box>
  )
})

export default JobApplicationPage
